import 
{
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Output,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from "@angular/core";
import 
{
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ApsService, CompanyService, ContactService, CustomerService, UtilityService } from "src/app/services";
import
{
  APSMaster,
  APSPayment,
  BankInfo,
  ContactWeb,
  CreditCardInfo,
  OneTimePaymentRequest,
  PaymentReceiptItem,
  WebCompany,
  WebContractAgreement,
  WebContractLetter,
  WebCustomer,
  WebCustomerAgingVariables,
  CompanyClient,
} from "src/app/models";
import { interval, Subscription, combineLatest, of } from "rxjs";
import { CrossFieldErrorMatcher } from "src/app/shared/helpers/cross-field-error-matcher";
import { NewCardComponent } from "../new-card/new-card.component";
import { NewBankComponent } from "../new-bank/new-bank.component";
import { MatDialog } from "@angular/material/dialog";
import { PaymentSubmittedDialogComponent } from "./payment-submitted-dialog/payment-submitted-dialog.component";
import { AddressComponent, AddressInfo } from "../address";
import { ContractsService } from "../../../services/contracts.service";
import { SharedService } from "src/app/services";
import { ErrorDialogComponent } from "../../../components/error-dialog/error-dialog.component";
import { SpinnerService } from "../../../services/spinner.service";
import { ProcessingDialogComponent } from "../../../components/processing-dialog/processing-dialog.component";
import { MatStepper } from "@angular/material/stepper";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ChaseSuccessDialogComponent } from "./chase-success-dialog/chase-success-dialog.component";
import { Router } from "@angular/router"
import { ChaseFailureDialogComponent } from "./chase-failure-dialog/chase-failure-dialog.component";
import { startWith, switchMap } from "rxjs/operators";
import { SessionDataService } from "src/app/services/session-data.service";
import { ErrorLoggingService } from "src/app/services/error-logging.service";
import { FormValidationService } from "src/app/services/form-validation.service";


@Component({
  selector: "app-make-a-payment",
  templateUrl: "./make-a-payment.component.html",
  styleUrls: ["./make-a-payment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MakeAPaymentComponent implements OnInit, OnDestroy 
{
  @Input() prebuy: boolean;
  @Input() contractTotal: number;
  @Input() contractsAdded: WebContractLetter[];
  @Input() cODOrderAmount: number;
  @Input() isPaymentForCODDelivery: boolean;

  // saveCard: boolean; RDC
  forceSubmit: boolean;

  defaultEmail: string = "";
  editEmail: boolean;
  isEmployee: boolean = false;

  displayName: string = "";

  isSubmitted: boolean = false;

  convenienceFeeActive: boolean;

  private availableBanksSubscription: Subscription;
  public availableBanks: APSMaster[];
  public showSpinner: boolean = false;

  //for polling
  private timeInterval: Subscription;

  private availableCardsSubscription: Subscription;
  public availableCards: APSMaster[];
  private companySubscription: Subscription;
  private customerAndDataLogicalSubscription: Subscription;
  public customer: WebCustomer;
  public convenienceFee: number;
  public convenienceFeeTax: number;
  public convenienceFeeDescription: string;
  public fees: number;
  contact: ContactWeb;
  contactList: ContactWeb[];
  contactSubscription: Subscription;
  companyInterfacesSubscription: Subscription;
  private agingSubscription: Subscription;
  public aging: WebCustomerAgingVariables;

  public amountFormGroup: FormGroup;
  public paymentMethodFormGroup: FormGroup;
  public reviewFormGroup: FormGroup;
  public chaseFormGroup: FormGroup;
  public errorMatcher = new CrossFieldErrorMatcher(["invalidAmount"]);

  public showNewCC: boolean = false;
  public showNewBank: boolean = false;
  public master: APSMaster = null;
  public cardInfo: CreditCardInfo = null;
  public bankInfo: BankInfo = null;

  //Chase hosted form variables
  public readonly displayChaseForm: boolean = false; //set to readonly to prevent chase form from showing up, use otp for chase payments until
  //further notice - kyg 5/17/23 
  public chaseFormUrl: string = "";
  private uid: string;
  public formUrl: SafeResourceUrl;
  private sanitizer: DomSanitizer;
  private dataLogical: string;
  private chaseLastFour: string;
  private chaseRefNo: string;
  private chaseCardExpiration: string;
  private displayChaseInfo: boolean;
  private chaseCardType: string;
  private unsuccessfulChasePayment: boolean;
  private chaseOtp: OneTimePaymentRequest;
  private company: WebCompany;

  @ViewChild("newCard") public newCardElement: NewCardComponent;
  @ViewChild("newBank") public newBankElement: NewBankComponent;
  @ViewChild("address") public addressElement: AddressComponent;
  @ViewChild("stepper") stepper: MatStepper;

  @Output() doneEvent = new EventEmitter();


  constructor(
    public apsService: ApsService,
    public utilityService: UtilityService,
    private formBuilder: FormBuilder,
    public customerService: CustomerService,
    public companyService: CompanyService,
    public dialog: MatDialog,
    private formValidationService: FormValidationService,
    private contractService: ContractsService,
    public sharedService: SharedService,
    private contactService: ContactService,
    private spinnerService: SpinnerService,
    private sessionDataService: SessionDataService,
    private errorLoggingService: ErrorLoggingService,
    private cdRef: ChangeDetectorRef,
    private companyClient: CompanyClient,
    private domSanitizer: DomSanitizer,
    private router: Router,
  ) { }

  transform(value: any, args?: any): any
  {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }
  ngOnInit(): void
  {
    this.sanitizer = this.domSanitizer;
    this.isEmployee = this.sessionDataService.IsEmployee;
    this.amountFormGroup = this.formBuilder.group(
      {
        accountAmount: [0, [Validators.required, Validators.min(0), this.moneyAmountValidator()]],
        budgetAmount: [0, [Validators.required, Validators.min(0), this.moneyAmountValidator()]],
        contractAmount: [0, [Validators.required, Validators.min(0), this.moneyAmountValidator()]],
        orderAmount: [0, [Validators.required, Validators.min(this.cODOrderAmount), this.moneyAmountValidator()]]
      },
      {
        validators: [this.amountValidate()],
        updateOn: "blur"
      }
    );

    this.paymentMethodFormGroup = this.formBuilder.group
      ({
        paymentMethod: [[""], Validators.required],
      });

    this.reviewFormGroup = this.formBuilder.group({
      email: [this.defaultEmail],
      convenienceFee: [0, this.convenienceFee],
      convenienceFeeTax: [0, this.convenienceFeeTax],
      convenienceFeeDescription: [this.convenienceFeeDescription],
    });

    this.chaseFormGroup = this.formBuilder.group({
      chaseEmail: [this.defaultEmail,]
    });

    this.availableBanksSubscription = this.apsService.availableBanks.subscribe(
      (banks) =>
      {
        this.availableBanks = banks;
      });

    this.availableCardsSubscription = this.apsService.availableCards.subscribe(
      (cards) =>
      {
        this.availableCards = cards;
      });


    this.companySubscription = this.companyService.company.subscribe(c =>
    {
      this.company = c;
    });

    this.agingSubscription = this.customerService.customerAging.subscribe(aging =>
    {
      this.aging = aging;
      if (this.isPaymentForCODDelivery && this.aging)
      {
        this.amountFormGroup.get('accountAmount')?.setValue(this.aging.BalDueWithDisc);
      }
    });

    this.contactSubscription = this.contactService.contacts.subscribe(c =>
    {
      this.contactList = c;
    });
    if (this.contactList.length > 0)
    {
      if (this.contactList[0] == null || this.contactList[0].Email === "")
      {
        this.editEmail = true;
        this.defaultEmail = "none available";
      }
      else
      {
        this.editEmail = false;
        this.defaultEmail = this.contactList[0].Email;
        this.contact = this.contactList[0];
      }
    }
    else
    {
      this.editEmail = true;
    }

    const dataLogical$ = this.sessionDataService.DataLogical
      ? of(this.sessionDataService.DataLogical)
      : this.sharedService.dataLogical;

    this.customerAndDataLogicalSubscription = combineLatest([
      this.customerService.customer,
      dataLogical$
    ]).subscribe(([customer, dataLogical]) =>
    {
      this.customer = customer;
      this.dataLogical = dataLogical;
      this.callGetAppStartup();
    });
  }

  public checkFormValidity(form): void 
  {
    form?.markAllAsTouched?.();

    if (!this.formValidationService.checkFormValidity(form)) 
    {
      return;
    }
  }

  private callGetAppStartup()
  {
    if (this.companyInterfacesSubscription)
    {
      this.companyInterfacesSubscription.unsubscribe();
    }

    if (this.dataLogical && this.customer)
    {
      this.companyInterfacesSubscription = this.companyClient
        .getAppStartup(this.dataLogical, this.customer.BranchNo)
        .subscribe(
          c =>
          {
            this.convenienceFeeActive = c.CompanyInterfaces?.IsConvenienceFeeActive ?? false;
          },
          error =>
          {
            this.convenienceFeeActive = false;
            this.errorLoggingService.logError(error);
          }
        );
    }
    else
    {
      this.convenienceFeeActive = false;
    }
  }

  public getOTP()
  {
    this.convenienceFee = 0
    this.convenienceFeeTax = 0
    if (this.showNewCC && this.cardInfo)
    {
      const payment: APSPayment = {};
      payment.Application = "C";
      payment.Amount = this.paymentAmountBeforeFees;
      payment.PaymentMethod = "C";
      payment.CardType = this.cardInfo.Type;
      const otp: OneTimePaymentRequest = {
        APSPayment: payment,
      };
      if (this.convenienceFeeActive) 
      {
        this.apsService
          .getConvenienceFee(otp)
          .subscribe((response) =>
          {
            this.convenienceFee = response.APSPayment.ConvenienceFee ?? 0;
            this.convenienceFeeTax = response.APSPayment.ConvenienceFeeTax ?? 0;
            this.convenienceFeeDescription = response.APSPayment?.ConvenienceFeeDescription?.trim();
            this.fees = this.convenienceFee + this.convenienceFeeTax
          },
            () =>
            {
              this.convenienceFeeError();
            });
      }
    }

    if (this.master && this.master.PymtMethod === "C") 
    {
      const otp: OneTimePaymentRequest = {
        APSPayment: {
          Application: "C",
          Amount: this.paymentAmountBeforeFees,
          PaymentMethod: this.master.PymtMethod,
          CardType: this.master.CardType,
        },

      };
      if (this.convenienceFeeActive)
      {
        this.apsService
          .getConvenienceFee(otp)
          .subscribe((response) => 
          {
            this.convenienceFee = response.APSPayment.ConvenienceFee ?? 0;
            this.convenienceFeeTax = response.APSPayment.ConvenienceFeeTax ?? 0;
            this.convenienceFeeDescription = response.APSPayment?.ConvenienceFeeDescription?.trim();
            this.fees = this.convenienceFee + this.convenienceFeeTax
          },
            () =>
            {
              this.convenienceFeeError();
            });
      }
    }
    if (this.showNewBank && this.bankInfo) 
    {
      const payment: APSPayment = {};
      payment.Application = "B";
      payment.Amount = this.paymentAmountBeforeFees;
      payment.PaymentMethod = "B";
      payment.BankAccountType = this.bankInfo.AccountType;

      const otp: OneTimePaymentRequest =
      {
        APSPayment: payment,
      };
      if (this.convenienceFeeActive)
      {
        this.apsService
          .getConvenienceFee(otp)
          .subscribe((response) =>
          {
            this.convenienceFee = response.APSPayment.ConvenienceFee ?? 0;
            this.convenienceFeeTax = response.APSPayment.ConvenienceFeeTax ?? 0;
            this.convenienceFeeDescription = response.APSPayment?.ConvenienceFeeDescription?.trim();
            this.fees = this.convenienceFee + this.convenienceFeeTax
          },
            () =>
            {
              this.convenienceFeeError();
            });
      }
    }

    if (this.master && this.master.PymtMethod === "B") 
    {
      const otp: OneTimePaymentRequest =
      {
        APSPayment:
        {
          Application: "B",
          Amount: this.paymentAmountBeforeFees,
          PaymentMethod: this.master.PymtMethod,
          BankAccountType: this.master.BankType,
        },

      };
      if (this.convenienceFeeActive)
      {
        this.apsService
          .getConvenienceFee(otp)
          .subscribe((response) => 
          {
            this.convenienceFee = response.APSPayment.ConvenienceFee ?? 0;
            this.convenienceFeeTax = response.APSPayment.ConvenienceFeeTax ?? 0;
            this.convenienceFeeDescription = response.APSPayment?.ConvenienceFeeDescription?.trim();
            this.fees = this.convenienceFee + this.convenienceFeeTax
          },
            () =>
            {
              this.convenienceFeeError();
            });
      }
    }
  }

  ngAfterViewChecked(): void
  {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void
  {
    this.availableBanksSubscription?.unsubscribe();
    this.availableCardsSubscription?.unsubscribe();
    this.companySubscription?.unsubscribe();
    this.customerAndDataLogicalSubscription?.unsubscribe();
    this.agingSubscription?.unsubscribe();
    this.contactSubscription?.unsubscribe();
    this.companyInterfacesSubscription?.unsubscribe();
  }

  private moneyAmountValidator(): ValidatorFn
  {
    return (control: AbstractControl): ValidationErrors | null =>
    {
      const text = String(control.value ?? "").trim();

      if (!text)
      {
        return null;
      }

      return this.isValidMoneyAmount(text)
        ? null
        : { invalidMoneyFormat: true };
    };
  }

  private isValidMoneyAmount(value: string): boolean
  {
    const parts = value.split(".");

    if (parts.length > 2)
    {
      return false;
    }

    const whole = parts[0];
    const decimal = parts[1];

    if (!whole)
    {
      return false;
    }

    if (!this.isDigitsOnly(whole))
    {
      return false;
    }

    if (whole.length > 1 && whole.startsWith("0"))
    {
      return false;
    }

    if (decimal !== undefined && decimal.length === 0)
    {
      return false;
    }

    if (decimal !== undefined && !this.isDigitsOnly(decimal))
    {
      return false;
    }

    return true;
  }

  private isDigitsOnly(value: string): boolean
  {
    return [...value].every(character => character >= "0" && character <= "9");
  }

  private amountValidate(): ValidatorFn
  {
    return (fg: FormGroup) =>
    {
      const account = fg.get("accountAmount").value ?? 0;
      const budget = fg.get("budgetAmount").value ?? 0;
      const contract = fg.get("contractAmount").value ?? 0;
      const codOrder = fg.get("orderAmount").value ?? 0;

      if (this.isPaymentForCODDelivery)
      {
        const fullAccountDue = this.aging?.BalDueWithDisc ?? 0;
        if (fullAccountDue <= 0)
        {
          if (codOrder < this.cODOrderAmount && account <= fullAccountDue)
          {
            return { min: true }
          }
        }
        if ((fullAccountDue > 0 && (account < fullAccountDue || codOrder < this.cODOrderAmount)))
        {
          return { codRequired: true };
        }
        return null;
      }
      else
      {
        if (account > 0 || budget > 0 || contract > 0)
        {
          return null;
        }

        return { invalidAmount: true };
      }
    }
  }

  public handleSelectionChanged(value: any): void 
  {
    this.showNewCC = false;
    this.showNewBank = false;
    this.master = null;
    if (value === "bank") 
    {
      this.showNewBank = true;
      this.removeCVV();
    }
    else if (value === "card") 
    {
      this.showNewCC = true;
      this.addCVV();
    }
    else 
    {
      const val = this.availableBanks
        .concat(this.availableCards)
        .find((e) => e.RecNo === Number(value));
      this.master = val;
      if (this.master.PymtMethod === "B") 
      {
        this.removeCVV();
        //if (this.company.ApsProcessor == 18) //read initial dispalyChaseForm variable assignment message near constructor - kyg
        //this.displayChaseForm = false;
      }
      else 
      {
        this.addCVV();
        //if(this.company.ApsProcessor == 18) //read initial displayChaseForm variable assignment message near constructor - kyg   
        //this.displayChaseForm = false;
      }
    }
  }

  private addCVV(): void 
  {
    this.reviewFormGroup.addControl(
      "cvv",
      new FormControl("")
    );
  }

  private removeCVV(): void 
  {
    this.reviewFormGroup.removeControl("cvv");
  }

  public handleNewCardNext(): void 
  {
    this.cardInfo = this.newCardElement.getCardInfo();
    this.setDisplayName();
  }

  public handleNewBankNext(): void 
  {
    this.bankInfo = this.newBankElement.getBankInfo();
    this.setDisplayName();
  }

  public handleReviewSubmit() 
  {
    this.isSubmitted = true;

    const dialogText: string = "Payment";

    const dialogRefProcessing = this.dialog.open(ProcessingDialogComponent,
      {
        data: dialogText,
        width: "400px",
        maxWidth: "90%",
        restoreFocus: false,
        disableClose: true,
      });

    const payment: APSPayment = {};
    payment.Application = "C";
    payment.Amount = this.paymentAmountBeforeFees;
    payment.ConvenienceFee = this.convenienceFee ?? 0;
    payment.ConvenienceFeeTax = this.convenienceFeeTax ?? 0;
    payment.ConvenienceFeeDescription = this.convenienceFeeDescription ?? "Convenience Fee";

    if (this.showNewCC && this.cardInfo) 
    {
      this.populateNewCardInfo(payment);
    }
    else if (this.showNewBank && this.bankInfo) 
    {
      this.populateNewBankInfo(payment);
    }
    else if (this.master) 
    {
      this.populateMasterInfo(payment);
    }
    const confEmail = this.editEmail ? this.email : this.defaultEmail;
    payment.DataLogical = this.dataLogical;

    const otp: OneTimePaymentRequest =
    {
      APSPayment: payment,
      AccountAmount: this.isPaymentForCODDelivery ? this.accountAmount + this.orderAmount : this.accountAmount,
      BudgetAmount: this.budgetAmount,
      ContractAmount: this.contractAmount,
      CustomerEmail: confEmail,
    };

    this.apsService.oneTimePayment(otp).subscribe(
      (o) =>
      {
        if (o)
        {
          if (this.isPaymentForCODDelivery)
          {
            this.sharedService.isPaymentForCODMade = true;
          }
          const letterAgreement: WebContractAgreement =
          {
            Company: this.company,
            Contracts: this.contractsAdded,
            CustomerEmail: "",
            CustomerName: ""
          }

          if (this.prebuy) 
          {
            this.contractService.writeContractLetter(letterAgreement);
          }
          dialogRefProcessing.close();
          this.dialog
            .open(PaymentSubmittedDialogComponent,
              {
                width: "450px",
                disableClose: true,
                data: { otpResponse: o, paymentInfo: otp },
              })
            .afterClosed()
            .subscribe((_) =>
            {
              this.isSubmitted = false;
              this.dialog.closeAll();
              this.customerService.populateData();
            });
        }
        const paymentReceiptItem: PaymentReceiptItem =
        {
          PaymentRequest: otp,
          PaymentResponseList: o
        };
        this.apsService.sendPaymentReceipt(paymentReceiptItem).subscribe(response =>
        {
          if (response != null && !response)
          {
            console.error("Email response was false.");
          }
        },
          (error) =>
          {
            this.errorLoggingService.logError(error)
          });
        // this is commented out until an issue with saving the card is fixed in v10 RDC 5/11/2023
        // if (this.newCardElement?.saveCard != null 
        //   && this.newCardElement?.saveCard != undefined 
        //   && this.newCardElement?.saveCard) {
        //   this.cardInfo = this.newCardElement.getCardInfo();
        //   this.cardInfo.Address1 = payment.Address1;
        //   this.cardInfo.Address2 = payment.Address2;
        //   this.cardInfo.City = payment.City;
        //   this.cardInfo.State = payment.State;
        //   this.cardInfo.Zip = payment.Zip;
        //   this.cardInfo.Type = payment.CardType;

        //   this.apsService.addCreditCard(this.cardInfo).subscribe(
        //     (b) => {
        //       if (b.valueOf()) {
        //         this.dialog.closeAll();
        //       }
        //     },
        //     (error) => {
        //       console.log("Error adding credit card: " + error);
        //       dialogRefProcessing.close();
        //     }
        //   );
        //   this.spinnerService.hide();
        // }
      },
      (error) => 
      {
        dialogRefProcessing.close();
        let email = this.company.WebEmail ? this.company.WebEmail : this.company.CorpEmail;
        const errorMsg: string =
          "There was an error processing your payment. Payment has not been submitted. If you have a question about this payment please contact " + this.company.Name + " \n Email: " + email + " \n Phone: " + this.company.Phone;
        this.dialog.open(ErrorDialogComponent,
          {
            data: errorMsg,
          }).afterClosed()
          .subscribe((_) =>
          {
            this.isSubmitted = false;
            this.dialog.closeAll();
            this.customerService.populateData();
          });
        this.isSubmitted = false;
      }
    );
    this.spinnerService.hide();
  }

  private populateAddressInfo(payment: APSPayment): void 
  {
    const addressInfo: AddressInfo = this.addressElement.getAddressInfo();
    payment.Address1 = addressInfo.Address1;
    payment.Address2 = addressInfo.Address2;
    payment.City = addressInfo.City;
    payment.State = addressInfo.State;
    payment.Zip = addressInfo.Zip;
  }

  private populateNewCardInfo(payment: APSPayment): void 
  {
    payment.RecNo = 0;
    payment.NameFirst = this.cardInfo.FirstName;
    payment.NameLast = this.cardInfo.LastName;
    payment.PaymentMethod = "C";
    payment.CardNo = Number(this.cardInfo.Number);
    payment.CardExprMonth = this.cardInfo.ExpirationMonth;
    payment.CardExprYear = this.cardInfo.ExpirationYear;
    payment.CardType = this.cardInfo.Type;
    payment.SecurityCode = this.reviewFormGroup.get("cvv").value;

    this.populateAddressInfo(payment);
  }

  private populateNewBankInfo(payment: APSPayment): void 
  {
    payment.RecNo = 0;
    payment.PaymentMethod = "B";
    payment.BankRoutingNumber = Number(this.bankInfo.RoutingNumber);
    payment.BankAccountNumber = this.bankInfo.AccountNumber;
    payment.BankAccountType = this.bankInfo.AccountType;
    payment.NameFirst = this.bankInfo.NameFirst;
    payment.NameLast = this.bankInfo.NameLast;

    this.populateAddressInfo(payment);
  }

  private populateMasterInfo(payment: APSPayment): void 
  {
    payment.RecNo = this.master.RecNo;
    if (this.master.PymtMethod === "C") 
    {
      payment.SecurityCode = this.reviewFormGroup.get("cvv").value;
    }

    payment.Address1 = this.master.Address1;
    payment.Address2 = this.master.Address2;
    payment.City = this.master.City;
    payment.State = this.master.St;
    payment.Zip = this.master.Zip;
    payment.NameFirst = this.master.NameFirst;
    payment.NameLast = this.master.NameLast;
    payment.CompanyName = this.master.CoName;
    payment.PaymentMethod = this.master.PymtMethod;
    payment.BranchNo = this.master.BranchNo;
    payment.CountryCode = this.master.CountryCd ?? "";

    if (this.master.PymtMethod === "B") 
    {
      payment.BankAccountNumber = this.master.BankAcct;
      payment.BankRoutingNumber = this.master.BankNo;
      payment.BankAccountType = this.master.BankType;
    }
    else 
    {
      payment.CardNo = this.master.CardNo;
      payment.CardType = this.master.CardType;
      payment.CardExprMonth = this.master.CardExprMonth;
      payment.CardExprYear = this.master.CardExprYear;
    }
  }

  get accountAmount(): number 
  {
    if (this.prebuy)
      return this.aging.BalDueWithDisc;
    return this.amountFormGroup.get("accountAmount").value;
  }

  get budgetAmount(): number 
  {
    return this.amountFormGroup.get("budgetAmount").value;
  }

  get contractAmount(): number 
  {
    if (this.prebuy)
      return this.contractTotal;
    return this.amountFormGroup.get("contractAmount").value;
  }

  get orderAmount(): number 
  {
    return this.amountFormGroup.get("orderAmount").value;
  }

  get email(): string 
  {
    return this.reviewFormGroup.get("email").value;
  }

  get chaseEmail(): string 
  {
    return this.chaseFormGroup.get("chaseEmail").value;
  }

  get paymentAmountBeforeFees(): number 
  {
    if (this.prebuy)
      return this.contractAmount + this.aging.BalDueWithDisc;
    return this.accountAmount + this.budgetAmount + this.contractAmount + this.orderAmount;
  }

  get finalAmountSentToProcessor(): number 
  {
    if (this.prebuy)
      return this.contractAmount + this.aging.BalDueWithDisc;
    if (this.convenienceFee > 0 || this.convenienceFeeTax > 0)
      return this.paymentAmountBeforeFees + this.fees;
    else
      return this.paymentAmountBeforeFees;
  }

  setDisplayName() 
  {
    if (this.showNewCC && this.cardInfo) 
    {
      this.displayName = this.cardInfo.FirstName + " " + this.cardInfo.LastName;
      this.getOTP();
    }
    else if (this.showNewBank && this.bankInfo) 
    {
      this.displayName = this.bankInfo.NameFirst + " " + this.bankInfo.NameLast;
      this.getOTP();
    }
    else if (this.master) 
    {
      this.displayName =
        this.master.CoName !== null && this.master.CoName !== ""
          ? this.master.CoName
          : this.master.NameFirst + " " + this.master.NameLast;
      this.getOTP();
    }
  }
  addressFilled() 
  {
    return (this.addressElement.getAddressInfo().Address1.length > 1 || this.addressElement.getAddressInfo().Address2.length > 1)
  }

  //This method is for the Chase Paymentech form
  bankClicked() 
  {
    //this.displayChaseForm = false;  //read initial dispalyChaseForm variable assignment message near constructor - kyg
  }

  //This method is for the Chase Paymentech form
  creditCardClicked() 
  {
    //if (this.company.ApsProcessor == 18)
    //this.displayChaseForm = true;   //read initial dispalyChaseForm variable assignment message near constructor - kyg
  }

  setChaseForm() 
  {
    const iframeEle = document.getElementById('chaseForm');
    const loadingEle = document.getElementById('loading');

    iframeEle.addEventListener('load', function ()
    {
      loadingEle.style.display = 'none';
      iframeEle.style.opacity = '1';
    })
    if (this.displayChaseForm && this.addressFilled()) 
    {
      const payment: APSPayment = {};
      payment.Application = "C";
      payment.Amount = this.paymentAmountBeforeFees;
      payment.ConvenienceFee = this.convenienceFee ?? 0;
      payment.ConvenienceFeeTax = this.convenienceFeeTax ?? 0;
      let Address = this.addressElement.getAddressInfo();
      payment.Address1 = Address.Address1;
      payment.Address2 = Address.Address2;
      payment.City = Address.City;
      payment.Zip = Address.Zip;
      payment.State = Address.State;
      payment.Id = this.customer.CustId;

      const otp: OneTimePaymentRequest =
      {
        CustomerEmail: "",
        AccountAmount: this.accountAmount,
        BudgetAmount: this.budgetAmount,
        ContractAmount: this.contractAmount,
        Company: this.company,
        APSPayment:
        {
          Application: payment.Application,
          CardType: 'V', //hard coding visa for convenience fee calculation - no way to determine card type with chase hosted form
          PaymentMethod: 'C',
          CompanyName: this.dataLogical,
          NameFirst: payment.NameFirst,
          NameLast: payment.NameLast,
          Address1: payment.Address1,
          Address2: payment.Address2,
          City: payment.City,
          State: payment.State,
          Zip: payment.Zip,
          Amount: this.paymentAmountBeforeFees,
          Id: payment.Id,
          BranchNo: payment.BranchNo,
        }
      };
      if (this.convenienceFeeActive)
      {
        this.apsService
          .getConvenienceFee(otp)
          .subscribe((response) => 
          {
            this.convenienceFee = response.APSPayment.ConvenienceFee ?? 0;
            this.convenienceFeeTax = response.APSPayment.ConvenienceFeeTax ?? 0;
            this.convenienceFeeDescription = response.APSPayment?.ConvenienceFeeDescription?.trim();
            this.fees = this.convenienceFee + this.convenienceFeeTax;
            otp.APSPayment.Amount += this.fees

          },
            () => 
            {
              this.convenienceFeeError();
            });
        this.chaseOtp = otp;
        this.apsService.getChasePage(otp).subscribe(url =>
        {
          this.uid = url.split('=')[1];
          this.chaseFormUrl = this.dataLogical.toLowerCase().includes("df") ? "https://www.chasepaymentechhostedpay-var.com/hpf/1_1/?" + url : "https://www.chasepaymentechhostedpay.com/hpf/1_1/?" + url;
          this.formUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chaseFormUrl);
          if (url != null)
          {
            this.pollChasePayment()
          }
        },
          () =>
          {
            this.loadChasePageError();
          });
      }
      else 
      {
        this.chaseOtp = otp;
        this.apsService.getChasePage(otp).subscribe(url => 
        {
          this.uid = url.split('=')[1];
          this.chaseFormUrl = this.dataLogical.toLowerCase().includes("df") ? "https://www.chasepaymentechhostedpay-var.com/hpf/1_1/?" + url : "https://www.chasepaymentechhostedpay.com/hpf/1_1/?" + url;
          this.formUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chaseFormUrl);
          if (url != null)
          {
            this.pollChasePayment()
          }
        },
          () => 
          {
            this.loadChasePageError();
          });
      }
    }
  }

  async pollChasePayment() 
  {
    let count = 0;
    let done = false;
    this.timeInterval = interval(1000) //milliseconds
      .pipe(
        startWith(10), //production value = 10
        switchMap(() => this.apsService.lookupChasePayment(this.dataLogical, this.uid))
      ).subscribe(res => 
      {
        count++;
        if (count > 300 && !done) //more than 5 minutes elapsed since uid generation
        {
          this.timeInterval.unsubscribe();
          res.message = null;
          this.dialog.closeAll();
          this.router.navigate([""]);
        }
        if (res.message != null) 
        {
          if (this.dataLogical.toLowerCase().includes("df")) 
          {
            this.forceSubmit = true; // for testing to force payment
          }
          if (res.message.includes("Success") && res.ApprovalCode.length > 1) 
          {
            if (!done) 
            { //this condition is necessary for preventing this logic from firing off in another polling subscription
              this.nextStep();
              done = true;
              this.chasePaymentComplete();
              this.timeInterval.unsubscribe();
            }
          }
        }
      }
      );
  }

  private convenienceFeeError(): void
  {
    const errorMsg: string =
      "There was a problem with the payment. Please try again later.";
    this.dialog.open(ErrorDialogComponent,
      {
        data: errorMsg
      }).afterClosed()
      .subscribe((_) =>
      {
        this.isSubmitted = false;
        this.dialog.closeAll();
        this.customerService.populateData();
      });
  }

  private loadChasePageError(): void
  {
    const errorMsg: string =
      "There was a problem with the server. Please try again later.";
    this.dialog.open(ErrorDialogComponent,
      {
        data: errorMsg
      }).afterClosed()
      .subscribe((_) => 
      {
        this.isSubmitted = false;
        this.dialog.closeAll();
        this.customerService.populateData();
      });
  }

  chasePaymentComplete() 
  {
    const confEmail = this.editEmail ? this.chaseEmail : this.defaultEmail;
    this.apsService.lookupChasePayment(this.dataLogical, this.uid).subscribe(res => 
    {
      try 
      {
        this.displayName = res.name;
        this.chaseLastFour = res.mPAN;
        this.chaseCardExpiration = `${ res.exp.substring(0, 2) }/${ res.exp.substring(2) }`;
        this.chaseRefNo = res.ApprovalCode;
        this.displayChaseInfo = true;
        this.chaseCardType = res.type;
        const payment: APSPayment = {};
        payment.Amount = res.total_amt
        payment.ConvenienceFee = this.convenienceFee ?? 0;
        payment.ConvenienceFeeTax = this.convenienceFeeTax ?? 0;
        payment.ConvenienceFeeDescription = this.convenienceFeeDescription ?? "Convenience Fee";
        payment.Address1 = res.customer_address;
        payment.City = res.customer_city;
        payment.State = res.customer_state;
        payment.Zip = res.customer_postal_code;
        payment.Application = "C";
        payment.CardNo = parseInt(this.chaseLastFour.replace(/X/g, ""));
        if (this.chaseCardType.toUpperCase().includes("VISA")) 
        {
          payment.CardType = "V";
        }
        else if (this.chaseCardType.toUpperCase().includes("MASTERCARD")) 
        {
          payment.CardType = "MC";
        }
        else 
        {
          payment.CardType = "D";
        }
        payment.NameFirst = res.customer_firstname;
        payment.NameLast = res.customer_lastname;
        payment.CompanyName = this.companyService.companyName;
        payment.PaymentMethod = "C";
        payment.RefNo = res.ApprovalCode;
        payment.RespMsg = res.TxnGUID;
        payment.Id = this.customer.CustId;
        payment.CardExprMonth = parseInt(res.exp.substring(0, 2));
        payment.CardExprYear = parseInt(res.exp.substring(2)) + 2000;
        payment.AccountNumber = this.customer.AcctNo;
        payment.SourceNo = this.customer.ApsSourceNo;
        // payment.BranchNo = this.customer.RunCode;
        this.chaseOtp.APSPayment = payment;
        this.chaseOtp.CustomerEmail = confEmail;

        this.chaseOtp.APSPayment.Successful = true;
        this.apsService.logChasePayment(this.chaseOtp.APSPayment).subscribe();
        // this.apsService.writeApsPayment(this.chaseOtp.APSPayment).subscribe(otpr => {

        //   this.utilityService.sendChaseEmailReceipt(this.chaseOtp).subscribe();

        //   if (otpr.Successful) {
        //     this.dialog.open(ChaseSuccessDialogComponent, {
        //       width: "450px",
        //       data: { otpResponse: otpr, paymentInfo: this.chaseOtp },

        //     }).afterClosed()
        //       .subscribe((_) => {
        //         this.isSubmitted = false;
        //         this.dialog.closeAll();
        //         this.customerService.populateData(); 
        //       });
        //   }
        //   else {     
        //     this.unsuccessfulChasePayment = true;
        //     this.chaseOtp.APSPayment.Successful = false;
        //     let email = this.company.WebEmail ? this.company.WebEmail : this.company.CorpEmail;
        //     this.dialog.open(ErrorDialogComponent, {            
        //       data: "There was an error processing your payment. Payment was submitted but not saved to your account. \n Please contact " + this.company.Name + " \n Email: "  + email  + " \n Phone: " +  this.company.Phone,
        //       width: "450px"
        //     }).afterClosed()
        //       .subscribe((_) => {
        //         this.isSubmitted = false;
        //         this.dialog.closeAll();
        //       });
        //   }
        // });
      }
      catch 
      {
        this.unsuccessfulChasePayment = true;
        this.dialog.open(ChaseFailureDialogComponent, {
          width: "450px"
        }).afterClosed()
          .subscribe((_) =>
          {
            this.isSubmitted = false;
            this.dialog.closeAll();
          });
      }
    });
  }
  nextStep() 
  {
    this.stepper.next();
  }
};
